import React, { useState, Component } from 'react';
import { useTranslation } from 'react-i18next';
import { BuildingIcon, UserIcon, SearchIcon, Loader2Icon, CheckCircle2Icon, LandmarkIcon, SmartphoneIcon } from 'lucide-react';
import { SearchableSelect } from '../../../shared/ui/SearchableSelect';
import { profileCountry, profileCity, profileStreet, makeCompanyName } from '../../../shared/config/localeProfile';
import { THAI_PROVINCES, THAI_BANKS } from '../mocks/mockSuppliers';
export function SupplierSelfService() {
  const {
    t
  } = useTranslation('suppliers');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [supplierType, setSupplierType] = useState<'company' | 'individual'>('company');
  // DBD Lookup State
  const [isLookingUp, setIsLookingUp] = useState(false);
  const [lookupSuccess, setLookupSuccess] = useState(false);
  const [lookupError, setLookupError] = useState('');
  const [dbdMeta, setDbdMeta] = useState<{
    registerDate?: string;
    juristicStatus?: string;
    juristicType?: string;
    isMockFallback?: boolean;
  }>({});
  const [formData, setFormData] = useState({
    // Shared
    email: '',
    phone: '',
    useAsBillingEmail: true,
    addressLine1: '',
    addressLine2: '',
    city: '',
    province: '',
    country: profileCountry(),
    postcode: '',
    // Company
    taxId: '',
    companyNameTh: '',
    companyNameEn: '',
    branch: '00000',
    vatRegistered: false,
    contactPerson: '',
    // Individual
    idType: 'citizen_id',
    idNumber: '',
    fullNameTh: '',
    fullNameEn: '',
    individualTaxId: '',
    // Payout method
    payoutMethod: 'bank_account',
    bankName: '',
    accountNumber: '',
    accountName: '',
    promptPayType: 'phone',
    promptPayId: '',
    promptPayName: ''
  });
  const handleLookup = async () => {
    if (!formData.taxId || formData.taxId.length !== 13) {
      setLookupError('Please enter a valid 13-digit Tax ID / Registration Number');
      setLookupSuccess(false);
      return;
    }
    setLookupError('');
    setIsLookingUp(true);
    setLookupSuccess(false);
    setDbdMeta({});
    const DBD_API_BASE = 'https://opendata.dbd.go.th/api/action/datastore_search';
    const resourceId = 'f008dbbf-ddfa-4e3a-bac4-358a1a2b9853';
    const apiUrl = `${DBD_API_BASE}?resource_id=${resourceId}&filters=${encodeURIComponent(JSON.stringify({
      juristicID: formData.taxId
    }))}&limit=1`;
    try {
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          Accept: 'application/json'
        }
      });
      if (!response.ok) throw new Error(`API returned status ${response.status}`);
      const data = await response.json();
      if (!data.success || !data.result?.records?.length) {
        setIsLookingUp(false);
        setLookupError(`No company found for Tax ID ${formData.taxId}. Please verify the number and try again.`);
        return;
      }
      const record = data.result.records[0];
      const companyNameLocal = record.juristicNameTH || record.juristicNameTh || record.companyNameTH || '';
      const companyNameEn = record.juristicNameEN || record.juristicNameEn || record.companyNameEN || '';
      const addressFull = record.addressDesc || record.address || record.juristicAddress || '';
      const registerDate = record.registerDate || record.registrationDate || '';
      const juristicStatus = record.juristicStatus || record.status || '';
      const juristicType = record.juristicType || record.entityType || '';
      let addressLine1 = addressFull;
      let addressLine2 = '';
      let city = '';
      let postcode = '';
      const postcodeMatch = addressFull.match(/\b(\d{5})\b\s*$/);
      if (postcodeMatch) {
        postcode = postcodeMatch[1];
        addressLine1 = addressFull.replace(postcodeMatch[0], '').trim();
      }
      const parts = addressLine1.split(/[,;]/).map((s: string) => s.trim()).filter(Boolean);
      if (parts.length >= 3) {
        addressLine1 = parts.slice(0, Math.ceil(parts.length / 2)).join(', ');
        const remaining = parts.slice(Math.ceil(parts.length / 2));
        city = remaining.pop() || '';
        addressLine2 = remaining.join(', ');
      } else if (parts.length === 2) {
        addressLine1 = parts[0];
        city = parts[1];
      }
      setFormData(prev => ({
        ...prev,
        companyNameTh: companyNameLocal || prev.companyNameTh,
        companyNameEn: companyNameEn || prev.companyNameEn,
        addressLine1: addressLine1 || prev.addressLine1,
        addressLine2: addressLine2 || prev.addressLine2,
        city: city || prev.city,
        postcode: postcode || prev.postcode
      }));
      setDbdMeta({
        registerDate,
        juristicStatus,
        juristicType,
        isMockFallback: false
      });
      setIsLookingUp(false);
      setLookupSuccess(true);
    } catch (err: any) {
      console.warn('DBD API call failed, using mock fallback:', err.message);
      setFormData(prev => ({
        ...prev,
        companyNameTh: makeCompanyName('Test Company'),
        companyNameEn: makeCompanyName('Test Company'),
        addressLine1: profileStreet(0),
        addressLine2: '',
        city: profileCity(0),
        postcode: '50470'
      }));
      setDbdMeta({
        registerDate: '2020-03-15',
        juristicStatus: 'Active',
        juristicType: 'Limited Company',
        isMockFallback: true
      });
      setIsLookingUp(false);
      setLookupSuccess(true);
    }
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const {
      name,
      value,
      type
    } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitted(true);
  };
  if (isSubmitted) {
    return <div className="min-h-screen bg-[#1a2042] flex flex-col items-center justify-center p-4" data-id="element-14282" data-mp="2l08ak2hm9tv4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center" data-id="element-14283" data-mp="2p1to7q1z6c6d">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-14284" data-mp="2txipbxtn814s">
            <CheckCircle2Icon size={32} className="text-green-600" data-id="element-14285" data-mp="2yvu1if7eunq6" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-14286" data-mp="33wrgyk11d3nz">
            {t('pages.SupplierSelfService.details_submitted_successfully')}
          </h2>
          <p className="text-gray-600 mb-6" data-id="element-14287" data-mp="3cp1qdz43x546">
            {t('pages.SupplierSelfService.thank_you_your_information_has_been')}
          </p>
          <p className="text-sm text-gray-500" data-id="element-14288" data-mp="3hnmre0r6nsto">
            {t('pages.SupplierSelfService.you_can_close_this_page')}
          </p>
        </div>
      </div>;
  }
  return <div className="min-h-screen bg-[#1a2042] py-12 px-4 sm:px-6 lg:px-8" data-id="element-14289" data-mp="3rnne7wo9gr2m">
      <div className="max-w-3xl mx-auto" data-id="element-14290" data-mp="1fzs1jehuo6mn">
        {/* Header */}
        <div className="text-center mb-8" data-id="element-14291" data-mp="1fww21lqct9w3">
          <div className="flex items-center justify-center mb-6" data-id="element-14292" data-mp="1azw5c1fe9jhn">
            <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/akmkk18tu8vmgxet3nfyip/2db2784c-a6fd-4102-a1f3-1a3d11f02729/63313e770ee1f10008cdcca5_Pomelo-Primary-logo-white.png" alt={t('pages.SupplierSelfService.attrs.pomelo')} className="h-10" data-id="element-14293" data-mp="1612u6fnky4kn" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-3" data-id="element-14294" data-mp="1trygix99o7ki">
            {t('pages.SupplierSelfService.complete_your_supplier_profile')}
          </h1>
          <p className="text-gray-400" data-id="element-14295" data-mp="1k0u15wfoikp5">
            {t('pages.SupplierSelfService.siam_fresh_market_co_ltd_has')}
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-lg overflow-hidden" data-id="element-14296" data-mp="2hgztmxt1o6w4">
          <div className="px-8 py-6 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-14297" data-mp="2cozlwj6qg9oz">
            <h2 className="text-lg font-semibold text-gray-900" data-id="element-14298" data-mp="1l150tny11dtr">
              {t('pages.SupplierForm.supplier_information')}
            </h2>

            {/* Supplier Type Segmented Control */}
            <div className="flex p-1 bg-gray-100 rounded-lg" data-id="element-14299" data-mp="1c2jk7yc488vm">
              <button type="button" onClick={() => setSupplierType('company')} className={`flex items-center px-4 py-1.5 text-sm font-medium rounded-md transition-all ${supplierType === 'company' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-14300" data-mp="1h038zs6fw41t">
                <BuildingIcon size={16} className="mr-2" data-id="element-14301" data-mp="1uqybmmqchowy" />
                {t('pages.SupplierDetail.company')}
              </button>
              <button type="button" onClick={() => setSupplierType('individual')} className={`flex items-center px-4 py-1.5 text-sm font-medium rounded-md transition-all ${supplierType === 'individual' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-14302" data-mp="1zsxbvnqsg5po">
                <UserIcon size={16} className="mr-2" data-id="element-14303" data-mp="btuxv9sdw780" />
                {t('pages.SupplierDetail.individual')}
              </button>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="p-8 space-y-8" data-id="element-14304" data-mp="ktqgu0s6l6il">
            {/* --- COMPANY FIELDS --- */}
            {supplierType === 'company' && <div className="space-y-6" data-id="element-14305" data-mp="3ta33z06ppjyd">
                {/* DBD Lookup Section */}
                <div className="bg-gray-50 p-5 rounded-xl border border-gray-200" data-id="element-14306" data-mp="3p8zo4donznx5">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-14307" data-mp="6u8lt3oakx0s">
                    {t('pages.SupplierForm.tax_id_registration_number')}
                  </label>
                  <div className="flex gap-3" data-id="element-14308" data-mp="3aisgyu43xrbr">
                    <input type="text" name="taxId" required maxLength={13} value={formData.taxId} onChange={handleChange} placeholder={t('pages.SupplierForm.attrs.13digit_registration_number')} className="flex-1 px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors bg-white" data-id="element-14309" data-mp="3ahhd6uanwjkn" />

                    <button type="button" onClick={handleLookup} disabled={isLookingUp} className="flex items-center px-5 py-2.5 bg-[#E2231A] hover:bg-[#EB4A42] disabled:bg-[#E2231A]/70 text-white text-sm font-medium rounded-lg transition-colors shadow-sm" data-id="element-14310" data-mp="3422pfq6e83ly">
                      {isLookingUp ? <Loader2Icon size={18} className="animate-spin" data-id="element-14311" data-mp="2lccw77ia3pw5" /> : <>
                          <SearchIcon size={18} className="mr-2" data-id="element-14312" data-mp="vtenyys47t2v" />
                          {t('pages.SupplierForm.lookup')}
                        </>}
                    </button>
                  </div>

                  {lookupError && <p className="mt-2 text-sm text-red-600" data-id="element-14313" data-mp="14umdarcnljkt">{lookupError}</p>}

                  {lookupSuccess && <div className="mt-4 space-y-2" data-id="element-14314" data-mp="d2wz55aelhms">
                      <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start" data-id="element-14315" data-mp="d4hugx4jl4lp">
                        <CheckCircle2Icon size={20} className="text-yellow-700 mt-0.5 mr-3 flex-shrink-0" data-id="element-14316" data-mp="h25tab9cvhiv" />

                        <div data-id="element-14317" data-mp="qvctzit5i9cm">
                          <p className="text-sm font-medium text-yellow-800" data-id="element-14318" data-mp="3pk1x32dgg80y">
                            {t('pages.SupplierSelfService.company_details_fetched_from_department_of')}
                          </p>
                          <div className="mt-1 flex items-center flex-wrap gap-x-3 gap-y-1 text-xs text-yellow-700" data-id="element-14319" data-mp="11mtu6d98albu">
                            {dbdMeta.registerDate && <>
                                <span data-id="element-14320" data-mp="1aj2ojdicmtr4">
                                  {t('pages.SupplierForm.registered')}{' '}
                                  {dbdMeta.registerDate}
                                </span>
                                <span className="w-1 h-1 rounded-full bg-yellow-400" data-id="element-14321" data-mp="irn4nr86fl4l"></span>
                              </>}
                            {dbdMeta.juristicStatus && <span className="font-semibold" data-id="element-14322" data-mp="mrnlfiir08et">
                                {dbdMeta.juristicStatus}
                              </span>}
                            {dbdMeta.juristicType && <>
                                <span className="w-1 h-1 rounded-full bg-yellow-400" data-id="element-14323" data-mp="1y8x2jyeemocy"></span>
                                <span data-id="element-14324" data-mp="26po07fn8juui">{dbdMeta.juristicType}</span>
                              </>}
                          </div>
                        </div>
                      </div>
                    </div>}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-14325" data-mp="21p078ofg53xn">
                  <div data-id="element-14326" data-mp="2pf40c1v0dlae">
                    <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-14327" data-mp="2peksxf2zifam">
                      {t('pages.SupplierForm.company_name_local')}
                    </label>
                    <input type="text" name="companyNameTh" required value={formData.companyNameTh} onChange={handleChange} className={`w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors ${lookupSuccess ? 'bg-yellow-50' : ''}`} data-id="element-14328" data-mp="2fn7yqgtprd1f" />
                  </div>
                  <div data-id="element-14329" data-mp="1kf614l7jjqyn">
                    <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-14330" data-mp="1kenzta60umjv">
                      {t('pages.SupplierDetail.company_name_english')}
                    </label>
                    <input type="text" name="companyNameEn" value={formData.companyNameEn} onChange={handleChange} className={`w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors ${lookupSuccess ? 'bg-yellow-50' : ''}`} data-id="element-14331" data-mp="1ajcq569h05iz" />
                  </div>
                  <div data-id="element-14332" data-mp="2qw1dbrww7d2z">
                    <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-14333" data-mp="2qxcnqakufj32">
                      {t('pages.SupplierForm.branch_number')}
                    </label>
                    <input type="text" name="branch" required value={formData.branch} onChange={handleChange} placeholder="00000" className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors" data-id="element-14334" data-mp="2th90wsgl8ycv" />
                  </div>
                  <div data-id="element-14335" data-mp="3heogo334omij">
                    <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-14336" data-mp="3hdwlgtdg0qss">
                      {t('pages.SupplierForm.contact_person_name')}
                    </label>
                    <input type="text" name="contactPerson" value={formData.contactPerson} onChange={handleChange} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors" data-id="element-14337" data-mp="3r480bayo0keg" />
                  </div>
                </div>

                <label className="flex items-center cursor-pointer group w-fit" data-id="element-14338" data-mp="1w2mjaejir8sd">
                  <div className="relative flex items-center justify-center" data-id="element-14339" data-mp="29trk3jymi74a">
                    <input type="checkbox" name="vatRegistered" checked={formData.vatRegistered} onChange={handleChange} className="peer sr-only" data-id="element-14340" data-mp="251hmriv6i4rz" />

                    <div className="w-5 h-5 border-2 border-gray-300 rounded bg-white peer-checked:bg-[#E2231A] peer-checked:border-[#E2231A] transition-colors" data-id="element-14341" data-mp="2srlp7mwmryhe"></div>
                    <svg className="absolute w-3.5 h-3.5 text-white pointer-events-none opacity-0 peer-checked:opacity-100 transition-opacity" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" data-id="element-14342" data-mp="2oqidnclm8olz">
                      <polyline points="20 6 9 17 4 12" data-id="element-14343" data-mp="ai8zappmr16r"></polyline>
                    </svg>
                  </div>
                  <span className="ml-3 text-sm font-medium text-gray-900 group-hover:text-gray-700" data-id="element-14344" data-mp="fgsnkwrp4p2a">
                    {t('pages.SupplierForm.vat_registered_company')}
                  </span>
                </label>
              </div>}

            {/* --- INDIVIDUAL FIELDS --- */}
            {supplierType === 'individual' && <div className="space-y-6" data-id="element-14345" data-mp="3nqluk4udxdu7">
                <div className="flex gap-6 mb-2" data-id="element-14346" data-mp="3snklkeir54o6">
                  <label className="flex items-center cursor-pointer" data-id="element-14347" data-mp="3sodvfod0caaj">
                    <input type="radio" name="idType" value="citizen_id" checked={formData.idType === 'citizen_id'} onChange={handleChange} className="w-4 h-4 text-[#E2231A] border-gray-300 focus:ring-[#E2231A]" data-id="element-14348" data-mp="1lx4m4yyfk8ye" />

                    <span className="ml-2 text-sm font-medium text-gray-700" data-id="element-14349" data-mp="2pr3kst7n7try">
                      {t('pages.SupplierForm.thai_citizen_id')}
                    </span>
                  </label>
                  <label className="flex items-center cursor-pointer" data-id="element-14350" data-mp="2gqf6dxec7rej">
                    <input type="radio" name="idType" value="passport" checked={formData.idType === 'passport'} onChange={handleChange} className="w-4 h-4 text-[#E2231A] border-gray-300 focus:ring-[#E2231A]" data-id="element-14351" data-mp="2grqci3v99b8t" />

                    <span className="ml-2 text-sm font-medium text-gray-700" data-id="element-14352" data-mp="515rwkz2ywmh">
                      {t('pages.SupplierForm.passport')}
                    </span>
                  </label>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-14353" data-mp="1ivumivjrc8j7">
                  <div className="md:col-span-2" data-id="element-14354" data-mp="1nvh1b9f8hwu4">
                    <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-14355" data-mp="1nu611jkzz77r">
                      {formData.idType === 'citizen_id' ? 'Citizen ID Number *' : 'Passport Number *'}
                    </label>
                    <input type="text" name="idNumber" required maxLength={formData.idType === 'citizen_id' ? 13 : undefined} value={formData.idNumber} onChange={handleChange} placeholder={formData.idType === 'citizen_id' ? '13-digit ID number' : 'Passport number'} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors" data-id="element-14356" data-mp="zz3an76jovp6" />
                  </div>
                  <div data-id="element-14357" data-mp="30iz5zy2pq8y3">
                    <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-14358" data-mp="30ktn11av5gww">
                      {t('pages.SupplierForm.full_name_local')}
                    </label>
                    <input type="text" name="fullNameTh" value={formData.fullNameTh} onChange={handleChange} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors" data-id="element-14359" data-mp="27vmsseifzdhg" />
                  </div>
                  <div data-id="element-14360" data-mp="3t7ef63z5xx1i">
                    <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-14361" data-mp="3t7wmpwr9zxoq">
                      {t('pages.SupplierForm.full_name')}
                    </label>
                    <input type="text" name="fullNameEn" required value={formData.fullNameEn} onChange={handleChange} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors" data-id="element-14362" data-mp="1mim7554tlgsz" />
                  </div>
                  <div className="md:col-span-2" data-id="element-14363" data-mp="12tlm3s12dr6w">
                    <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-14364" data-mp="ytjru3xns01n">
                      {t('pages.SupplierForm.tax_id_if_different_from_citizen')}
                    </label>
                    <input type="text" name="individualTaxId" value={formData.individualTaxId} onChange={handleChange} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors" data-id="element-14365" data-mp="ox6zb9duns8h" />
                  </div>
                </div>
              </div>}

            {/* --- SHARED CONTACT FIELDS --- */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 pt-4 border-t border-gray-100" data-id="element-14366" data-mp="7onwnwyrk4tq">
              <div data-id="element-14367" data-mp="cm7aivrjufl5">
                <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-14368" data-mp="cjl7n9flso99">
                  {t('pages.SupplierForm.email_address')}
                </label>
                <input type="email" name="email" required value={formData.email} onChange={handleChange} placeholder={t('pages.SupplierForm.attrs.enter_email_address')} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors" data-id="element-14369" data-mp="15a361x5du1gc" />
              </div>
              <div data-id="element-14370" data-mp="1y6ui61j0gi9a">
                <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-14371" data-mp="1t66sbj6iq7gn">
                  {t('pages.SupplierForm.phone_number')}
                </label>
                <input type="tel" name="phone" value={formData.phone} onChange={handleChange} placeholder={t('pages.SupplierForm.attrs.eg_0812345678')} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors" data-id="element-14372" data-mp="274wo8vhlq13f" />
              </div>
            </div>

            {/* Divider: Billing information */}
            <div className="relative py-2" data-id="element-14373" data-mp="1jeai59r5qqw2">
              <div className="absolute inset-0 flex items-center" data-id="element-14374" data-mp="vprcz33emu87">
                <div className="w-full border-t border-gray-200" data-id="element-14375" data-mp="roxoo8m1vdes"></div>
              </div>
              <div className="relative flex justify-center" data-id="element-14376" data-mp="rn4lx4ek5dc2">
                <span className="bg-white px-4 text-sm font-medium text-gray-500 uppercase tracking-wider" data-id="element-14377" data-mp="14frhprbxuw4o">
                  {t('pages.SupplierForm.billing_information')}
                </span>
              </div>
            </div>

            {/* Billing Info Fields */}
            <div className="space-y-5" data-id="element-14378" data-mp="1j3cj9gt60nah">
              <input type="text" name="addressLine1" value={formData.addressLine1} onChange={handleChange} placeholder={t('pages.SupplierForm.attrs.address_line_1')} className={`w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors ${lookupSuccess && supplierType === 'company' ? 'bg-yellow-50' : ''}`} data-id="element-14379" data-mp="1j4dxfu5h9pwf" />

              <input type="text" name="addressLine2" value={formData.addressLine2} onChange={handleChange} placeholder={t('pages.SupplierForm.attrs.address_line_2')} className={`w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors ${lookupSuccess && supplierType === 'company' ? 'bg-yellow-50' : ''}`} data-id="element-14380" data-mp="2qntcnecz1ms" />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-14381" data-mp="3his5d0j2uw37">
                <div data-id="element-14382" data-mp="j1y0md4t5wdd">
                  <label className="block text-xs font-medium text-gray-500 mb-1" data-id="element-14383" data-mp="j2hgloaweaor">
                    {t('pages.SupplierForm.city_district')}
                  </label>
                  <input type="text" name="city" value={formData.city} onChange={handleChange} placeholder={t('pages.SupplierForm.attrs.eg_khlong_toei')} className={`w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors ${lookupSuccess && supplierType === 'company' ? 'bg-yellow-50' : ''}`} data-id="element-14384" data-mp="941s7t399fiy" />
                </div>
                <div data-id="element-14385" data-mp="1kw9dlpce07ge">
                  <label className="block text-xs font-medium text-gray-500 mb-1" data-id="element-14386" data-mp="16ykrke6l2u9r">
                    {t('pages.BulkSupplierUpload.province')}
                  </label>
                  <SearchableSelect name="province" options={THAI_PROVINCES} value={formData.province} onChange={val => setFormData(prev => ({
                  ...prev,
                  province: val
                }))} placeholder={t('pages.SupplierForm.attrs.select_province')} data-id="element-14387" data-mp="1bzs2gm40w9kn" />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-14388" data-mp="ec9isg8tpn1v">
                <div className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm bg-gray-50 text-gray-700 flex items-center" data-id="element-14389" data-mp="j8i5qft9ikjl">
                  {t('pages.SupplierForm.thailand')}
                </div>
                <input type="text" name="postcode" value={formData.postcode} onChange={handleChange} placeholder={t('pages.SupplierForm.attrs.postcode_5_digits')} className={`w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors ${lookupSuccess && supplierType === 'company' ? 'bg-yellow-50' : ''}`} data-id="element-14390" data-mp="3rstu6rtgtc61" />
              </div>
            </div>

            {/* Divider: Payout method */}
            <div className="relative py-2" data-id="element-14391" data-mp="ndc64bxqn5b0">
              <div className="absolute inset-0 flex items-center" data-id="element-14392" data-mp="129ti95dr4nym">
                <div className="w-full border-t border-gray-200" data-id="element-14393" data-mp="127yxps6e6xv2"></div>
              </div>
              <div className="relative flex justify-center" data-id="element-14394" data-mp="xctbstczpj6z">
                <span className="bg-white px-4 text-sm font-medium text-gray-500 uppercase tracking-wider" data-id="element-14395" data-mp="3e4tkvawyh8ba">
                  {t('pages.SupplierDetail.payout_method')}
                </span>
              </div>
            </div>

            <p className="text-xs text-gray-500 -mt-4" data-id="element-14396" data-mp="3o2r8bw6zr12u">
              {t('pages.SupplierSelfService.choose_how_you_would_like_to')}
            </p>

            {/* Payout Method Selection */}
            <div className="space-y-4" data-id="element-14397" data-mp="2vf3ssvkkv0n2">
              {/* Bank Account Option */}
              <div className={`border rounded-xl transition-colors ${formData.payoutMethod === 'bank_account' ? 'border-[#E2231A] ring-1 ring-[#E2231A]' : 'border-gray-200'}`} data-id="element-14398" data-mp="30dosmtvx3iaz">
                <label className="flex items-center p-4 cursor-pointer hover:bg-gray-50" data-id="element-14399" data-mp="3a38ca1rgfg6w">
                  <input type="radio" name="payoutMethod" value="bank_account" checked={formData.payoutMethod === 'bank_account'} onChange={handleChange} className="w-4 h-4 text-[#E2231A] border-gray-300 focus:ring-[#E2231A] mt-0.5" data-id="element-14400" data-mp="2clzyd7zjcjre" />

                  <div className="ml-3 flex items-center" data-id="element-14401" data-mp="ppto1g5d586i">
                    <div className="w-8 h-8 rounded-full bg-yellow-50 flex items-center justify-center mr-3" data-id="element-14402" data-mp="psfvzgw61pp9">
                      <LandmarkIcon size={16} className="text-yellow-700" aria-hidden="true" data-id="element-14403" data-mp="ktmelddrdejt" />
                    </div>
                    <div data-id="element-14404" data-mp="6zv7screw8rs">
                      <div className="text-sm font-medium text-gray-900" data-id="element-14405" data-mp="6z29m06iqrl9">
                        {t('pages.SupplierDetail.bank_account')}
                      </div>
                      <div className="text-xs text-gray-500" data-id="element-14406" data-mp="3t721zowyznvy">
                        {t('pages.SupplierForm.direct_transfer_to_a_thai_bank')}
                      </div>
                    </div>
                  </div>
                </label>

                {formData.payoutMethod === 'bank_account' && <div className="p-4 bg-gray-50 border-t border-gray-100 space-y-4 rounded-b-xl" data-id="element-14407" data-mp="2t4uat2qtat1t">
                    <div data-id="element-14408" data-mp="3743ex41qtwpf">
                      <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-14409" data-mp="373agqrguofiw">
                        {t('pages.SupplierDetail.bank_name')}
                      </label>
                      <SearchableSelect name="bankName" options={THAI_BANKS} value={formData.bankName} onChange={val => setFormData(prev => ({
                    ...prev,
                    bankName: val
                  }))} placeholder={t('pages.SupplierForm.attrs.select_a_bank')} data-id="element-14410" data-mp="2ef6940epxsdk" />
                    </div>
                    <div data-id="element-14411" data-mp="68b6410wgvre">
                      <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-14412" data-mp="6702q2bd9q0t">
                        {t('pages.SupplierDetail.account_number')}
                      </label>
                      <input type="text" name="accountNumber" maxLength={12} placeholder={t('pages.SupplierForm.attrs.1012_digits')} value={formData.accountNumber} onChange={handleChange} className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm focus:ring-1 focus:ring-[#E2231A] focus:border-[#E2231A]" data-id="element-14413" data-mp="3tdah5qj4j9a0" />
                    </div>
                    <div data-id="element-14414" data-mp="kve2g4dhkyd5">
                      <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-14415" data-mp="kz1uez3a9cpr">
                        {t('pages.SupplierDetail.account_name')}
                      </label>
                      <input type="text" name="accountName" placeholder={t('pages.SupplierForm.attrs.name_on_the_bank_account')} value={formData.accountName} onChange={handleChange} className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm focus:ring-1 focus:ring-[#E2231A] focus:border-[#E2231A]" data-id="element-14416" data-mp="35ohk1qqrmvdt" />
                    </div>
                  </div>}
              </div>

              {/* PromptPay Option */}
              <div className={`border rounded-xl transition-colors ${formData.payoutMethod === 'promptpay' ? 'border-[#E2231A] ring-1 ring-[#E2231A]' : 'border-gray-200'}`} data-id="element-14417" data-mp="1ttdwftyr6sm4">
                <label className="flex items-center p-4 cursor-pointer hover:bg-gray-50" data-id="element-14418" data-mp="3o86s1szmzje2">
                  <input type="radio" name="payoutMethod" value="promptpay" checked={formData.payoutMethod === 'promptpay'} onChange={handleChange} className="w-4 h-4 text-[#E2231A] border-gray-300 focus:ring-[#E2231A] mt-0.5" data-id="element-14419" data-mp="3j8ao1tqxvryi" />

                  <div className="ml-3 flex items-center" data-id="element-14420" data-mp="4f4k80gziwdo">
                    <div className="w-8 h-8 rounded-full bg-purple-50 flex items-center justify-center mr-3" data-id="element-14421" data-mp="9ajwwp39am8i">
                      <SmartphoneIcon size={16} className="text-purple-600" data-id="element-14422" data-mp="37v1yaki9ooha" />
                    </div>
                    <div data-id="element-14423" data-mp="3cstz640w3kxm">
                      <div className="text-sm font-medium text-gray-900" data-id="element-14424" data-mp="3hroeux3k8tdi">
                        {t('pages.SupplierDetail.promptpay')}
                      </div>
                      <div className="text-xs text-gray-500" data-id="element-14425" data-mp="3lutemrw9agyg">
                        {t('pages.SupplierForm.fast_transfer_via_phone_citizen_id')}
                      </div>
                    </div>
                  </div>
                </label>

                {formData.payoutMethod === 'promptpay' && <div className="p-4 bg-gray-50 border-t border-gray-100 space-y-4 rounded-b-xl" data-id="element-14426" data-mp="unar06g6q3p6">
                    <div data-id="element-14427" data-mp="19kkzd6xj6tgo">
                      <label className="block text-xs font-medium text-gray-700 mb-2" data-id="element-14428" data-mp="19kt9ws66lmop">
                        {t('pages.SupplierForm.promptpay_type')}
                      </label>
                      <div className="flex flex-wrap gap-4" data-id="element-14429" data-mp="1ijogi5d50ysj">
                        <label className="flex items-center" data-id="element-14430" data-mp="1dlmzdpz9v9jp">
                          <input type="radio" name="promptPayType" value="phone" checked={formData.promptPayType === 'phone'} onChange={handleChange} className="w-4 h-4 text-[#E2231A] border-gray-300 focus:ring-[#E2231A]" data-id="element-14431" data-mp="1dl3pmwjrzrgr" />

                          <span className="ml-2 text-sm text-gray-700" data-id="element-14432" data-mp="1x7jcpvqctlad">
                            {t('pages.SupplierForm.phone_number_2')}
                          </span>
                        </label>
                        <label className="flex items-center" data-id="element-14433" data-mp="2kun8frz3yxz1">
                          <input type="radio" name="promptPayType" value="citizen_id" checked={formData.promptPayType === 'citizen_id'} onChange={handleChange} className="w-4 h-4 text-[#E2231A] border-gray-300 focus:ring-[#E2231A]" data-id="element-14434" data-mp="26wgh6uonzw8c" />

                          <span className="ml-2 text-sm text-gray-700" data-id="element-14435" data-mp="1o4nq55t5ol39">
                            {t('pages.SupplierForm.citizen_id')}
                          </span>
                        </label>
                        <label className="flex items-center" data-id="element-14436" data-mp="1eank91q1vbth">
                          <input type="radio" name="promptPayType" value="tax_id" checked={formData.promptPayType === 'tax_id'} onChange={handleChange} className="w-4 h-4 text-[#E2231A] border-gray-300 focus:ring-[#E2231A]" data-id="element-14437" data-mp="1j6ceqqn7q2ix" />

                          <span className="ml-2 text-sm text-gray-700" data-id="element-14438" data-mp="3779doiww1t4o">
                            {t('pages.BulkSupplierUpload.tax_id')}
                          </span>
                        </label>
                      </div>
                    </div>
                    <div data-id="element-14439" data-mp="3uu7tded6ox1j">
                      <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-14440" data-mp="3utpra13ubolp">
                        {t('pages.SupplierDetail.promptpay_id')}
                      </label>
                      <input type="text" name="promptPayId" placeholder={formData.promptPayType === 'phone' ? '0XX-XXX-XXXX' : 'X-XXXX-XXXXX-XX-X'} value={formData.promptPayId} onChange={handleChange} className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm focus:ring-1 focus:ring-[#E2231A] focus:border-[#E2231A]" data-id="element-14441" data-mp="2jgvk2nynnm7z" />
                    </div>
                    <div data-id="element-14442" data-mp="2n325nuibm93y">
                      <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-14443" data-mp="2s2eqk9pmt63w">
                        {t('pages.SupplierForm.display_name_for_verification')}
                      </label>
                      <input type="text" name="promptPayName" value={formData.promptPayName} onChange={handleChange} className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm focus:ring-1 focus:ring-[#E2231A] focus:border-[#E2231A]" data-id="element-14444" data-mp="2wzn7k6z1n43k" />
                    </div>
                  </div>}
              </div>
            </div>

            {/* Form Actions */}
            <div className="pt-6 border-t border-gray-100" data-id="element-14445" data-mp="3jaevdtkrgp9v">
              <button type="submit" className="w-full px-6 py-3 bg-[#E2231A] hover:bg-[#EB4A42] text-white text-sm font-medium rounded-lg transition-colors shadow-sm" data-id="element-14446" data-mp="3jbzpji35y677">
                {t('pages.SupplierSelfService.submit_details')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>;
}